<template>
  <div
    class="tw-rounded-sm tw-bg-white tw-p-4"
    :data-test-id="`${tier}-phishing-simulation-lures-chart`"
  >
    <div class="tw-flex">
      <div class="tw-mb-6 tw-flex">
        <span
          v-tippy="fishHookToolTipp"
          class="tw-mr-2 tw-w-7 tw-h-7 tw-p-0.5 tw-rounded-full tw-flex tw-items-center"
          :data-test-id="`${tier}-phishing-simulation-lures-chart-icon`"
        >
          <cb-icon-phishing />
        </span>
        <cl-heading class="tw-flex tw-items-center" :size="'h6'">
          {{ $t("lures-sent-vs-clicked-title") }}
        </cl-heading>
      </div>
      <span
        v-tippy="infoToolTipp"
        class="tw-text-primary-grey-200 tw-ml-auto"
        :data-test-id="`${tier}-phishing-simulation-lures-chart-info-icon`"
      >
        <cb-icon-information :width="18" />
      </span>
    </div>
    <div class="tw-relative tw-h-72 tw-w-full">
      <div
        v-if="loading"
        class="tw-h-full tw-w-full tw-animate-pulse tw-rounded-sm tw-bg-primary-grey-50/40"
      ></div>
      <canvas ref="canvasRef" width="100" height="auto" role="image"></canvas>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";

import Chart, { type Chart as ChartType } from "chart.js/auto";

import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { useLuresStore } from "./useLures.store";

import type { LuresSentVsClicked } from "@/modules/PhishingSimulations/store/types";

const style = getComputedStyle(document.body);
const textColor = style.getPropertyValue("--chartWhite");

const blue = {
  default: "rgba(14, 156, 255, 1)",
  half: "rgba(14, 156, 255, 0.5)",
  quarter: "rgba(14, 156, 255, 0.25)",
  zero: "rgba(14, 156, 255, 0)",
};

const red = {
  default: "rgba(210, 63, 63, 1)",
  half: "rgba(210, 63, 63, 0.85)",
  quarter: "rgba(210, 63, 63, 0.5)",
  zero: "rgba(210, 63, 63, 0.25)",
};
const { isMspRoute } = useIsMspRoute();

const tier = isMspRoute.value ? "msp" : "customer";
const { t } = useI18n();
const luresStore = useLuresStore();
const { luresSentVsClicked } = storeToRefs(luresStore);
const { mspGlobalOrganisationId } = useGlobalOrganisationID();

const loading = ref(true);
const canvasRef = ref<HTMLCanvasElement | null>(null);
let chartInstance: ChartType | null = null;
let isUnmounted = false;

const luresSent = computed<number[]>(() =>
  luresSentVsClicked.value.map((item: LuresSentVsClicked) => item.sent),
);
const fishHookToolTipp = ref({
  content: t("phishing-simulation-label"),
  placement: "top",
});
const infoToolTipp = ref({
  content: t("lures-sent-vs-clicked-tooltip"),
  placement: "top",
});
const luresClicked = computed<number[]>(() =>
  luresSentVsClicked.value.map((item: LuresSentVsClicked) => item.clicked),
);

const labels = computed<string[]>(() =>
  luresSentVsClicked.value.map((item: LuresSentVsClicked) => {
    const date = new Date(item.date);
    const month = new Intl.DateTimeFormat("en", { month: "short" }).format(
      date,
    );
    const year = date.getFullYear();
    return `${month} ${year}`;
  }),
);

const getLuresSentVsClicked = async () => {
  try {
    loading.value = true;

    await luresStore.getLuresSentVsClicked({
      globalOrganisationID: mspGlobalOrganisationId.value,
    });
  } finally {
    loading.value = false;
    await nextTick();
    initChart();
  }
};

const initChart = () => {
  const canvas = canvasRef.value;

  if (!canvas || isUnmounted) {
    return;
  }

  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }

  const existing = Chart.getChart(canvas);
  if (existing) {
    existing.destroy();
  }

  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return;
  }

  const gradientBlue = ctx.createLinearGradient(0, 0, 0, 250);
  gradientBlue.addColorStop(0, blue.half);
  gradientBlue.addColorStop(0.35, blue.quarter);
  gradientBlue.addColorStop(1, blue.zero);

  const gradientRed = ctx.createLinearGradient(0, 0, 0, 250);
  gradientRed.addColorStop(0, red.half);
  gradientRed.addColorStop(0.35, red.quarter);
  gradientRed.addColorStop(1, red.zero);

  chartInstance = new Chart(ctx, {
    type: "line",
    data: {
      labels: labels.value,
      datasets: [
        {
          label: t("lures-sent-title"),
          data: luresSent.value,
          backgroundColor: gradientBlue,
          pointBackgroundColor: blue.default,
          borderColor: blue.default,
          borderWidth: 2,
          pointRadius: 3,
          tension: 0.4,
          fill: true,
          type: "line",
          yAxisID: "y",
        },
        {
          label: t("lures-clicked-title"),
          data: luresClicked.value,
          backgroundColor: gradientRed,
          pointBackgroundColor: red.default,
          borderColor: red.default,
          borderWidth: 2,
          pointRadius: 3,
          tension: 0.4,
          fill: true,
          type: "line",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawTicks: false,
          },
          border: {
            display: false,
          },
          ticks: {
            padding: 12,
            color: textColor,
          },
          offset: false,
        },
        y: {
          beginAtZero: true,
          position: "left",
          title: {
            display: true,
            text: t("lures-sent-title"),
            color: blue.default,
            font: {
              size: 18,
            },
          },
          border: {
            display: false,
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            padding: 12,
            precision: 0,
            color: textColor,
          },
        },
        y1: {
          position: "right",
          title: {
            display: true,
            text: t("lures-clicked-title"),
            color: red.default,
            font: {
              size: 18,
            },
          },
          border: {
            display: false,
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            display: false,
            padding: 12,
            precision: 0,
            color: textColor,
          },
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            padding: 24,
            color: textColor,
          },
        },
      },
    },
  });
};

onMounted(async () => {
  await getLuresSentVsClicked();
});

onBeforeUnmount(() => {
  isUnmounted = true;
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
});
</script>
